"use client";

/**
 * ThemeProvider Component
 *
 * A client component that applies tenant-specific colors as CSS custom properties
 * to the document root element. This enables real-time theme customization
 * throughout the application.
 *
 * @module ThemeProvider
 */

import { useEffect } from 'react';
import {
  generateCSSVariables,
  applyCSSVariablesToRoot,
  removeCSSVariablesFromRoot,
  getAllCSSVariableNames,
  type TenantColors,
} from '@/lib/theme-utils';

/**
 * Props for the ThemeProvider component
 */
export interface ThemeProviderProps {
  /**
   * The tenant object containing color configuration.
   * Can be null/undefined if no tenant context is available.
   */
  tenant?: TenantColors | null;
  /**
   * Child components to render
   */
  children: React.ReactNode;
}

/**
 * ThemeProvider applies tenant-specific colors as CSS custom properties to :root.
 *
 * This component should be placed high in the component tree, typically wrapping
 * the main application content. It will:
 * - Apply CSS variables on mount based on tenant colors
 * - Update CSS variables when tenant colors change
 * - Use default colors when tenant colors are not set
 * - Clean up CSS variables on unmount
 *
 * @param props - Component props
 * @returns The children wrapped with theme context
 *
 * @example
 * ```tsx
 * // In layout.tsx or a parent component
 * import { ThemeProvider } from '@/components/ThemeProvider';
 *
 * export default function Layout({ children, tenant }) {
 *   return (
 *     <ThemeProvider tenant={tenant}>
 *       {children}
 *     </ThemeProvider>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // With tenant colors
 * const tenant = {
 *   primaryColor: '#FF0000',
 *   secondaryColor: '#00FF00',
 *   accentColor: '#0000FF',
 *   backgroundColor: '#FFFFFF',
 *   textColor: '#000000',
 *   footerBackgroundColor: '#333333',
 * };
 *
 * <ThemeProvider tenant={tenant}>
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({ tenant, children }: ThemeProviderProps) {
  useEffect(() => {
    // Generate CSS variables from tenant colors (uses defaults for missing values)
    const cssVariables = generateCSSVariables(tenant);

    // Apply CSS variables to the document root
    applyCSSVariablesToRoot(cssVariables);

    // Cleanup: remove CSS variables when component unmounts
    // This ensures we don't leave stale theme values
    return () => {
      removeCSSVariablesFromRoot(getAllCSSVariableNames());
    };
    // Intentionally listing individual color properties as dependencies
    // instead of the `tenant` object to prevent unnecessary re-renders
    // when only the object reference changes but colors remain the same.
    // This enables real-time preview updates when individual colors change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenant?.primaryColor,
    tenant?.secondaryColor,
    tenant?.accentColor,
    tenant?.backgroundColor,
    tenant?.textColor,
    tenant?.footerBackgroundColor,
  ]);

  // Simply render children - the theme is applied via side effects
  return <>{children}</>;
}

/**
 * Default export for convenience
 */
export default ThemeProvider;
